// src/hooks/useScheduleRecommendations.ts
import { useState, useEffect, useRef } from 'react';
import { Task } from '../types/task';
import { TimeRecommendation } from '../types/scheduleRecommendation';
import { scheduleRecommendationService } from '../services/api/scheduleRecommendations';
import { logDev, logError } from '../utils/logger';

interface UseScheduleRecommendationsOptions {
  /** 是否自動獲取建議（預設 true） */
  autoFetch?: boolean;

  /** 成功回調 */
  onSuccess?: (recommendations: TimeRecommendation[]) => void;

  /** 錯誤回調 */
  onError?: (error: Error) => void;
}

interface UseScheduleRecommendationsResult {
  /** AI 建議列表 */
  recommendations: TimeRecommendation[];

  /** 載入狀態 */
  isLoading: boolean;

  /** 錯誤訊息 */
  error: string | null;

  /** 手動觸發獲取建議 */
  fetchRecommendations: () => Promise<void>;

  /** 重置狀態 */
  reset: () => void;
}

/**
 * AI 時間建議 Hook
 * 管理 AI 建議的獲取、狀態和錯誤處理
 *
 * @param task 任務資訊
 * @param userId 用戶 ID
 * @param options 可選配置
 * @returns 建議狀態和控制函數
 */
export function useScheduleRecommendations(
  task: Task | null,
  userId: string,
  options: UseScheduleRecommendationsOptions = {}
): UseScheduleRecommendationsResult {
  const { autoFetch = true, onSuccess, onError } = options;

  const [recommendations, setRecommendations] = useState<TimeRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 使用 ref 來追蹤是否已經發起過請求
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * 獲取 AI 建議
   */
  const fetchRecommendations = async () => {
    // 如果沒有任務資訊，不發起請求
    if (!task) {
      logDev('ScheduleRecommendations', '任務資訊為空，跳過請求');
      return;
    }

    // 取消之前的請求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      logDev('ScheduleRecommendations', '開始獲取 AI 建議', {
        taskId: task.id,
        taskTitle: task.title
      });

      const result = await scheduleRecommendationService.getRecommendations({
        task: {
          id: task.id,
          title: task.title,
          category: task.category,
          estimatedTime: task.estimatedTime,
          priority: task.priority,
          energyLevel: task.energyLevel,
          notes: task.notes
        },
        userId,
        options: {
          // currentDate 由後端計算，不再由前端傳遞
          count: 3
        }
      });

      setRecommendations(result);
      setIsLoading(false);

      logDev('ScheduleRecommendations', 'AI 建議獲取成功', {
        count: result.length
      });

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      // 如果是取消請求，不設置錯誤狀態
      if (err instanceof Error && err.name === 'AbortError') {
        logDev('ScheduleRecommendations', '請求已取消');
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setIsLoading(false);

      logError('獲取 AI 時間建議失敗', err);

      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMessage));
      }
    }
  };

  /**
   * 重置狀態
   */
  const reset = () => {
    setRecommendations([]);
    setIsLoading(false);
    setError(null);
    hasFetchedRef.current = false;

    // 取消正在進行的請求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  /**
   * 自動獲取建議（僅在 autoFetch 為 true 且尚未獲取時）
   */
  useEffect(() => {
    if (autoFetch && task && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchRecommendations();
    }

    // 清理函數：取消正在進行的請求
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [task, userId, autoFetch]);

  return {
    recommendations,
    isLoading,
    error,
    fetchRecommendations,
    reset
  };
}
